/**
 * v1.14.74.0 — FASE 7E: Standalone Applications audit page.
 *
 * Tabella full audit con filtri + paginazione + CSV export.
 * URL: /admin/ai-site-doctor/applications
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Loader2, Download, RefreshCw, ArrowLeft, Filter,
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/ai-site-doctor`;

const STATUS_BADGE = {
  applied:                   "bg-emerald-100 text-emerald-800",
  apply_failed:              "bg-rose-100 text-rose-800",
  apply_failed_rolled_back:  "bg-amber-100 text-amber-800",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminAISiteDoctorApplicationsPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ final_status: "", auto_apply: "" });
  const [expanded, setExpanded] = useState(null);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [rollbackReason, setRollbackReason] = useState("");
  const [rollbackBusy, setRollbackBusy] = useState(false);

  const submitRollback = async () => {
    if (rollbackReason.trim().length < 10) {
      toast.error("Motivo rollback ≥10 caratteri");
      return;
    }
    setRollbackBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/rollback/${rollbackTarget.apply_id}`,
        { rollback_reason: rollbackReason },
        { withCredentials: true });
      const okSha = data.sha256_mismatch_count === 0;
      const okTest = data.selftest_post_rollback_passed;
      if (okSha && okTest) {
        toast.success("Rollback completato. Sito sano.");
      } else if (okSha) {
        toast.warning("Rollback OK ma self-test post-rollback ha segnalato anomalie");
      } else {
        toast.error("Rollback con mismatch SHA256");
      }
      setRollbackTarget(null);
      setRollbackReason("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || e.message);
      toast.error("Rollback fallito: " + msg);
    } finally { setRollbackBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.final_status) params.final_status = filters.final_status;
      if (filters.auto_apply !== "") params.auto_apply = filters.auto_apply;
      const { data } = await axios.get(`${API}/applications`, {
        params, withCredentials: true });
      setItems(data.items || []);
      setStats(data.stats || null);
    } catch (e) {
      toast.error("Errore caricamento");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.final_status) params.append("final_status", filters.final_status);
      if (filters.auto_apply !== "") params.append("auto_apply", filters.auto_apply);
      const res = await axios.get(
        `${API}/applications-export.csv?${params.toString()}`,
        { responseType: "blob", withCredentials: true });
      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `fix-applications-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast.success("CSV scaricato");
    } catch (e) {
      toast.error("Export fallito");
    }
  };

  const counts = stats?.counts || {};

  return (
    <div data-testid="aisd-applications-page"
         className="min-h-screen bg-slate-50">
      <Toaster position="top-right" />

      <div className="bg-white border-b border-slate-200 px-4 sm:px-8 py-5">
        <div className="max-w-7xl mx-auto">
          <Link to="/admin/ai-site-doctor"
                 className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-900 mb-2">
            <ArrowLeft className="w-4 h-4" /> AI Site Doctor
          </Link>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
            Storico Applicazioni Fix
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Audit non cancellabile di tutte le applicazioni di fix proposal.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 space-y-5">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase text-slate-500">Totale</div>
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase text-slate-500">Success rate</div>
              <div className="text-2xl font-bold text-emerald-700">{stats.success_rate_pct}%</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase text-slate-500">Applied</div>
              <div className="text-2xl font-bold text-emerald-700">{counts.applied || 0}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase text-slate-500">Rolled back / Failed</div>
              <div className="text-2xl font-bold text-amber-700">
                {(counts.apply_failed_rolled_back || 0) + (counts.apply_failed || 0)}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
            <select data-testid="aisd-apps-filter-status"
                    value={filters.final_status}
                    onChange={(e) => setFilters({ ...filters, final_status: e.target.value })}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
              <option value="">Tutti</option>
              <option value="applied">Applied</option>
              <option value="apply_failed">Apply failed</option>
              <option value="apply_failed_rolled_back">Rolled back</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Modalità</label>
            <select data-testid="aisd-apps-filter-mode"
                    value={filters.auto_apply}
                    onChange={(e) => setFilters({ ...filters, auto_apply: e.target.value })}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
              <option value="">Tutte</option>
              <option value="false">Manual</option>
              <option value="true">Auto</option>
            </select>
          </div>
          <div className="flex gap-2 ml-auto">
            <button data-testid="aisd-apps-refresh"
                    onClick={load} disabled={loading}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Aggiorna
            </button>
            <button data-testid="aisd-apps-export-csv"
                    onClick={exportCsv}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-700 text-white rounded-lg text-sm hover:bg-emerald-800">
              <Download className="w-4 h-4" />
              Esporta CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div data-testid="aisd-apps-table"
              className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Quando</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">apply_id</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Applier</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Mode</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Test</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Tempo</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-700" />
                  </td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={8} className="py-12 text-center text-slate-400 text-sm">
                    Nessuna applicazione corrispondente ai filtri.
                  </td></tr>
                )}
                {!loading && items.map((a, i) => {
                  const tests = a.test_results || [];
                  const failed = tests.filter((t) => t.exit_code !== 0).length;
                  return (
                    <React.Fragment key={a.apply_id || i}>
                      <tr data-testid={`aisd-apps-row-${a.apply_id?.slice(0, 8)}`}
                          onClick={() => setExpanded(expanded === a.apply_id ? null : a.apply_id)}
                          className="border-b border-slate-100 hover:bg-emerald-50/30 cursor-pointer">
                        <td className="px-4 py-3 text-xs">{fmtDate(a.applied_at)}</td>
                        <td className="px-4 py-3 text-xs font-mono">{(a.apply_id || "").slice(0, 8)}…</td>
                        <td className="px-4 py-3 text-xs">{a.applier_id || "—"}</td>
                        <td className="px-4 py-3 text-xs">
                          <span className="px-1.5 py-0.5 bg-slate-100 rounded">
                            {a.auto_apply ? "Auto" : "Manual"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${STATUS_BADGE[a.final_status] || "bg-slate-100"}`}>
                            {a.final_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-xs">
                          <span className="text-emerald-700">{tests.length - failed}</span>/
                          <span className="text-rose-700">{failed}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-mono">{a.runtime_total_sec}s</td>
                        <td className="px-4 py-3 text-right">
                          {a.final_status === "applied" && (
                            <button data-testid={`aisd-rollback-btn-${a.apply_id?.slice(0, 8)}`}
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       setRollbackTarget(a);
                                       setRollbackReason("");
                                     }}
                                     className="text-xs px-2 py-1 bg-amber-100 text-amber-800 hover:bg-amber-200 rounded">
                              ↶ Rollback
                            </button>
                          )}
                          {a.final_status === "manually_rolled_back" && (
                            <span className="text-xs text-slate-500 italic">rolled back</span>
                          )}
                        </td>
                      </tr>
                      {expanded === a.apply_id && (
                        <tr className="bg-slate-50">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="text-xs space-y-2">
                              <div><strong>fix_id:</strong> <code>{a.fix_id}</code></div>
                              <div><strong>error_id:</strong> <code>{a.error_id}</code></div>
                              {a.approval_reason && (
                                <div><strong>Approval reason:</strong> {a.approval_reason}</div>
                              )}
                              <div><strong>Correlation ID:</strong> <code>{a.correlation_id}</code></div>
                              {tests.length > 0 && (
                                <details>
                                  <summary className="cursor-pointer font-medium">Test output ({tests.length})</summary>
                                  <div className="mt-2 space-y-1">
                                    {tests.map((t, idx) => (
                                      <div key={idx} className={`p-2 rounded ${t.exit_code === 0 ? "bg-emerald-100" : "bg-rose-100"}`}>
                                        <div className="font-mono mb-1">
                                          <strong>{t.exit_code === 0 ? "✓" : "✗"}</strong> {t.command}
                                        </div>
                                        {(t.stdout || t.stderr) && (
                                          <pre className="whitespace-pre-wrap max-h-32 overflow-y-auto">{(t.stdout || "") + (t.stderr || "")}</pre>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Rollback modal */}
      {rollbackTarget && (
        <div data-testid="aisd-rollback-modal"
              className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
              onClick={() => !rollbackBusy && setRollbackTarget(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Conferma rollback</h3>
            <p className="text-sm text-slate-700 mb-3">
              Confermi il rollback dell&apos;apply <code className="text-xs">{rollbackTarget.apply_id?.slice(0, 8)}…</code>?
              <br />
              I file verranno ripristinati allo stato pre-fix.
              <br />
              <span className="text-xs text-slate-500">Operazione 60-120 secondi (esegue self-test post-rollback).</span>
            </p>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Motivo del rollback (≥10 caratteri)
            </label>
            <textarea data-testid="aisd-rollback-reason"
                       value={rollbackReason}
                       onChange={(e) => setRollbackReason(e.target.value)}
                       rows={3}
                       placeholder="Es: Regressione in produzione, ripristino stato precedente"
                       className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                       autoFocus />
            <div className="text-xs text-slate-500 mt-1">{rollbackReason.trim().length}/10 caratteri minimo</div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRollbackTarget(null)}
                      disabled={rollbackBusy}
                      className="px-3 py-1.5 border border-slate-200 rounded text-sm hover:bg-slate-50 disabled:opacity-50">
                Annulla
              </button>
              <button data-testid="aisd-rollback-submit"
                      disabled={rollbackBusy || rollbackReason.trim().length < 10}
                      onClick={submitRollback}
                      className="px-3 py-1.5 bg-amber-600 text-white rounded text-sm hover:bg-amber-700 disabled:opacity-40">
                {rollbackBusy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : null}
                Esegui rollback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
